// ============================================================================
// CANCUN100 — FichaAgente.js  (Bloque 28: "Completa tu ficha", paso a paso, un dato a la vez)
// ----------------------------------------------------------------------------
// Lado agente (Sala y Top Tours). Barra de avance + "faltan N obligatorios". Cada paso se guarda al avanzar
// (upsert en agentes_ficha) para poder salir y seguir después; lo no enviado del paso actual vive en el
// borrador del Bloque 27 (sin CLABE). Funciona en celular (cámara) y en computadora.
// Al enviar con todos los obligatorios: agentes_ficha.completada_en y agentes.ficha_completa_en = now().
// modo 'completar' (primera pantalla) | 'corregir' (desde Mi panel, con botón Cerrar).
// ============================================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RADIO, RADIO_TARJETA, RADIO_AVATAR, campo as estiloCampo, etiqueta as estiloEtiqueta } from './tema';
import useBorrador, { claveBorrador, BandaBorrador } from './useBorrador';
import ModalError from './ModalError';
import { PASOS, TOTAL_PASOS, TIPOS_ID, TALLAS, PARENTESCOS, ETIQUETA_CAMPO, pasoCompleto, errorPaso, obligatoriosFaltantes, cargarFicha, guardarPasoFicha, enviarFicha, subirArchivoFicha, guardarFotoPerfil, urlFirmada, validarArchivo, soloDigitos, fmtFechaCorta } from './ficha';
import { Ic } from './iconos'; // APP 2/34: íconos Lucide en vez de emoji

const CAMPOS_TEXTO = ['nombre_completo', 'fecha_nacimiento', 'celular', 'correo_personal', 'direccion', 'emergencia_nombre', 'emergencia_telefono', 'emergencia_parentesco', 'identificacion_tipo', 'banco', 'clabe', 'titular_cuenta', 'talla_playera', 'notas'];
const INICIAL = Object.fromEntries(CAMPOS_TEXTO.map((c) => [c, '']));
const esVacio = (e) => !e || CAMPOS_TEXTO.every((c) => !String(e[c] || '').trim());
const noVacios = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => String(v == null ? '' : v).trim() !== ''));
// Al reabrir: primer paso OBLIGATORIO pendiente (los opcionales omitidos no detienen); si no falta nada, la revisión.
const primerPendiente = (ficha, agente) => { const i = PASOS.findIndex((p) => p.obligatorio && !pasoCompleto(p, ficha, agente)); return i < 0 ? TOTAL_PASOS - 1 : i; };

export default function FichaAgente({ C, agente, modo = 'completar', onActualizarAgente, onCerrar }) {
  const [ficha, setFicha] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [idx, setIdx] = useState(0);
  const [f, setF] = useState(INICIAL);           // texto del formulario (todos los pasos; se guarda por paso)
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [errorModal, setErrorModal] = useState(null);
  const [vistas, setVistas] = useState({});       // campo → URL firmada (vista previa)
  const [archivoNuevo, setArchivoNuevo] = useState(null); // File elegido en el paso actual (no se guarda en borrador)
  const [enviada, setEnviada] = useState(false);
  const fileRef = useRef(null);
  const agenteId = agente && agente.id;
  const quien = (agente && (agente.firma || agente.nombre)) || 'agente'; // Bloque 39: en sandbox firma el super admin

  const ponerF = useCallback((d) => setF((x) => ({ ...INICIAL, ...x, ...noVacios(d) })), []); // el borrador solo completa; nunca pisa lo guardado con vacíos
  // Borrador solo mientras la ficha NO está enviada (cada paso ya se guarda en la base; al corregir no hace falta).
  const borrador = useBorrador(claveBorrador('ficha', agenteId), f, ponerF, { activo: !!agenteId && !cargando && !enviada && !(agente && agente.ficha_completa_en), inicial: INICIAL, esVacio });

  // Cargar ficha + vistas previas de archivos
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const fi = agenteId ? await cargarFicha(agenteId) : null;
        if (!vivo) return;
        setFicha(fi);
        const base = { ...INICIAL };
        CAMPOS_TEXTO.forEach((c) => { if (fi && fi[c] != null) base[c] = String(fi[c]); });
        setF((prev) => ({ ...base, ...Object.fromEntries(Object.entries(prev).filter(([, v]) => String(v || '').trim() !== '')) }));
        setIdx(modo === 'corregir' ? 0 : primerPendiente(fi, agente));
        const rutas = { foto: agente && agente.foto_url, identificacion_url: fi && fi.identificacion_url, comprobante_domicilio_url: fi && fi.comprobante_domicilio_url, caratula_bancaria_url: fi && fi.caratula_bancaria_url };
        const vs = {};
        await Promise.all(Object.entries(rutas).map(async ([k, r]) => { if (r) { try { vs[k] = await urlFirmada(r, 600); } catch { /* sin vista */ } } }));
        if (vivo) setVistas(vs);
      } catch (e) {
        if (vivo) setErrorModal({ titulo: 'No se pudo cargar tu ficha', mensaje: 'Revisa tu conexión e intenta de nuevo.', detalle: e.message });
      } finally { if (vivo) setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [agenteId, modo]);

  const paso = PASOS[idx];
  const fichaVista = useMemo(() => ({ ...(ficha || {}), ...noVacios(f) }), [ficha, f]); // lo guardado + lo capturado (sin vacíos encima)
  const faltan = useMemo(() => obligatoriosFaltantes(fichaVista, agente), [fichaVista, agente]);
  const avance = Math.round(((idx) / (TOTAL_PASOS - 1)) * 100);
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setErr(''); };
  const campo = estiloCampo(C);
  const lbl = estiloEtiqueta(C);
  const btnPrimario = { height: 40, boxSizing: 'border-box', padding: '0 16px', fontSize: 14, fontWeight: 700, borderRadius: RADIO, border: 'none', background: 'var(--equipo-grad)', color: '#fff', cursor: busy ? 'wait' : 'pointer', fontFamily: 'var(--au-font-text)', flex: 1 };
  const btnGhost = { ...btnPrimario, background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, flex: '0 0 auto' };

  const elegirArchivo = (file) => {
    const e = validarArchivo(file, { pdf: paso.archivo !== 'foto' && paso.archivo !== 'identificacion_url' });
    if (e) { setErr(e); return; }
    setArchivoNuevo(file); setErr('');
    if (/^image\//.test(file.type)) { try { setVistas((v) => ({ ...v, [paso.archivo]: URL.createObjectURL(file) })); } catch { /* sin vista */ } }
  };

  // Guarda el paso actual (texto + archivo) y avanza. omitir=true salta un paso opcional sin validar.
  const guardarYSeguir = async (omitir = false) => {
    if (busy) return;
    const patch = {};
    paso.campos.forEach((c) => { patch[c] = String(f[c] || '').trim() || null; });
    if (patch.clabe != null) patch.clabe = soloDigitos(patch.clabe);
    if (!omitir) {
      const e = errorPaso(paso, { ...(ficha || {}), ...patch });
      if (e) { setErr(e); return; }
      const completo = paso.archivo
        ? (!!archivoNuevo || (paso.archivo === 'foto' ? !!(agente && agente.foto_url) : !!(ficha && ficha[paso.archivo]))) && paso.campos.every((c) => patch[c])
        : paso.campos.every((c) => patch[c]);
      if (paso.obligatorio && !completo) { setErr(paso.archivo ? 'Falta el archivo o un dato de este paso.' : 'Este dato es obligatorio.'); return; }
    }
    setBusy(true);
    try {
      let fichaNueva = ficha;
      let agentePatch = null;
      if (archivoNuevo) {
        if (paso.archivo === 'foto') { const ruta = await guardarFotoPerfil(agenteId, archivoNuevo); agentePatch = { foto_url: ruta }; }
        else { patch[paso.archivo] = await subirArchivoFicha(agenteId, paso.archivo, archivoNuevo); }
      }
      if (Object.keys(patch).length) fichaNueva = await guardarPasoFicha(agenteId, patch, quien);
      if (fichaNueva) setFicha(fichaNueva);
      if (agentePatch && onActualizarAgente) onActualizarAgente(agentePatch);
      setArchivoNuevo(null);
      setIdx((i) => Math.min(TOTAL_PASOS - 1, i + 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErrorModal({ titulo: 'No se pudo guardar este paso', mensaje: 'Lo que capturaste sigue aquí. Revisa tu conexión y vuelve a intentar.', detalle: e.message, reintentar: () => { setErrorModal(null); guardarYSeguir(omitir); } });
    } finally { setBusy(false); }
  };
  const enviar = async () => {
    if (busy || faltan.length) return;
    setBusy(true);
    try {
      const { ficha: fi, ficha_completa_en } = await enviarFicha(agenteId, quien);
      setFicha(fi); setEnviada(true); borrador.limpiar();
      if (onActualizarAgente) onActualizarAgente({ ficha_completa_en });
    } catch (e) {
      setErrorModal({ titulo: 'No se pudo enviar la ficha', mensaje: 'Tus datos ya están guardados paso por paso; solo falló el envío final.', detalle: e.message, reintentar: () => { setErrorModal(null); enviar(); } });
    } finally { setBusy(false); }
  };

  if (cargando) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Cargando tu ficha…</div>;

  const tarjeta = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, boxShadow: 'var(--au-shadow-card)', padding: 18, maxWidth: 560, margin: '0 auto' };

  if (enviada) {
    return (
      <div style={tarjeta}>
        <div style={{ fontSize: 30, textAlign: 'center' }}>{<Ic n="Check" chico />}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.text, textAlign: 'center', marginTop: 6 }}>Ficha enviada · v{(ficha && ficha.version) || 1}</div>
        <div style={{ fontSize: 14, color: C.muted, textAlign: 'center', marginTop: 6 }}>Gracias, {agente.nombre}. Ya puedes seguir vendiendo; tu ficha queda en "Mi panel" por si hay que corregir algo.</div>
        {onCerrar && <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}><button type="button" style={{ ...btnPrimario, flex: '0 0 auto' }} onClick={onCerrar}>Ir a Mi panel</button></div>}
      </div>
    );
  }

  // Piezas como FUNCIONES (no componentes): si fueran componentes definidos aquí, los inputs perderían el foco en cada tecla.
  const archivo = (clave, pdf = false) => {
    const vista = vistas[clave];
    const esPdfVista = vista && /\.pdf(\?|$)/i.test(vista) && !archivoNuevo;
    return (
      <div>
        {vista && !esPdfVista && (
          <img src={vista} alt={paso.titulo} onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: clave === 'foto' ? 140 : '100%', height: clave === 'foto' ? 140 : 'auto', maxHeight: 260, objectFit: 'cover', borderRadius: clave === 'foto' ? RADIO_AVATAR : RADIO, border: `1px solid ${C.border}`, display: 'block', margin: clave === 'foto' ? '0 auto 10px' : '0 0 10px' }} />
        )}
        {esPdfVista && <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{<Ic n="FileText" chico />} Ya hay un PDF guardado. <a href={vista} target="_blank" rel="noreferrer" style={{ color: C.blue }}>Verlo</a></div>}
        <input ref={fileRef} type="file" accept={pdf ? 'image/*,application/pdf' : 'image/*'} capture={clave === 'foto' ? 'user' : 'environment'} style={{ display: 'none' }}
          onChange={(e) => { const file = e.target.files && e.target.files[0]; if (file) elegirArchivo(file); e.target.value = ''; }} />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={{ ...btnPrimario, background: 'transparent', color: C.text, border: `1px solid ${C.border}` }} onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>
            {<Ic n="Camera" chico />} {vista ? 'Cambiar' : (clave === 'foto' ? 'Tomar o elegir foto' : (pdf ? 'Elegir foto o PDF' : 'Tomar o elegir foto'))}
          </button>
        </div>
        {archivoNuevo && <div style={{ fontSize: 12, color: C.green, marginTop: 6 }}>Listo: {archivoNuevo.name} · se sube al guardar</div>}
      </div>
    );
  };
  const texto = (c, { type = 'text', placeholder, inputMode, maxLength } = {}) => (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={lbl}>{ETIQUETA_CAMPO[c]}</span>
      <input type={type} inputMode={inputMode} maxLength={maxLength} value={f[c] || ''} placeholder={placeholder} onChange={(e) => set({ [c]: e.target.value })} style={campo} autoComplete="off" />
    </label>
  );
  const opciones = (c, lista) => (
    <div style={{ marginBottom: 12 }}>
      <span style={lbl}>{ETIQUETA_CAMPO[c]}</span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {lista.map(([v, l]) => (
          <button key={v} type="button" aria-pressed={f[c] === v} onClick={() => set({ [c]: v })}
            style={{ height: 36, boxSizing: 'border-box', padding: '0 12px', borderRadius: RADIO, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--au-font-text)', border: `1px solid ${f[c] === v ? 'transparent' : C.border}`, background: f[c] === v ? 'var(--equipo-grad)' : 'transparent', color: f[c] === v ? '#fff' : C.text }}>{l}</button>
        ))}
      </div>
    </div>
  );

  const cuerpo = () => {
    switch (paso.clave) {
      case 'foto': return archivo('foto');
      case 'nombre': return texto('nombre_completo', { placeholder: 'Como aparece en tu identificación' });
      case 'nacimiento': return texto('fecha_nacimiento', { type: 'date' });
      case 'celular': return texto('celular', { type: 'tel', inputMode: 'tel', placeholder: '998 000 0000' });
      case 'correo': return texto('correo_personal', { type: 'email', inputMode: 'email', placeholder: 'tucorreo@ejemplo.com' });
      case 'direccion': return (
        <label style={{ display: 'block', marginBottom: 12 }}>
          <span style={lbl}>{ETIQUETA_CAMPO.direccion}</span>
          <textarea rows={3} value={f.direccion || ''} onChange={(e) => set({ direccion: e.target.value })} placeholder="Calle, número, colonia, ciudad" style={{ ...campo, height: 'auto', minHeight: 80, padding: '10px 14px', resize: 'vertical' }} />
        </label>
      );
      case 'emergencia': return (<>
        {texto('emergencia_nombre', { placeholder: 'Nombre de la persona' })}
        {texto('emergencia_telefono', { type: 'tel', inputMode: 'tel', placeholder: '998 000 0000' })}
        {opciones('emergencia_parentesco', PARENTESCOS.map((p) => [p, p]))}
      </>);
      case 'identificacion': return (<>
        {opciones('identificacion_tipo', TIPOS_ID)}
        {archivo('identificacion_url')}
      </>);
      case 'comprobante': return archivo('comprobante_domicilio_url', true);
      case 'banco': return (<>
        {texto('banco', { placeholder: 'BBVA, Banorte, Santander…' })}
        {texto('clabe', { inputMode: 'numeric', maxLength: 18, placeholder: '18 dígitos' })}
        <div style={{ fontSize: 12, color: soloDigitos(f.clabe).length === 18 ? C.green : C.muted, marginTop: -8, marginBottom: 10 }}>{soloDigitos(f.clabe).length} / 18 dígitos</div>
        {texto('titular_cuenta', { placeholder: 'Nombre del titular' })}
      </>);
      case 'caratula': return archivo('caratula_bancaria_url', true);
      case 'talla': return opciones('talla_playera', TALLAS.map((t) => [t, t]));
      case 'revision': return (
        <div>
          {faltan.length > 0 ? (
            <div className="au-banner au-banner--error" style={{ fontSize: 12, marginBottom: 12 }}>
              Faltan {faltan.length} obligatorio{faltan.length !== 1 ? 's' : ''}: {faltan.map((p) => p.titulo).join(', ')}.
            </div>
          ) : <div className="au-banner au-banner--success" style={{ fontSize: 12, marginBottom: 12 }}>Todo lo obligatorio está completo.</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, fontSize: 14 }}>
            {PASOS.filter((p) => p.clave !== 'revision').map((p) => {
              const ok = pasoCompleto(p, fichaVista, agente);
              return (
                <button key={p.clave} type="button" onClick={() => setIdx(p.n - 1)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: RADIO, padding: '8px 12px', color: C.text, cursor: 'pointer', fontFamily: 'var(--au-font-text)', textAlign: 'left' }}>
                  <span>{ok ? '✅' : (p.obligatorio ? '⚠️' : '○')} {p.titulo}{!p.obligatorio && <span style={{ color: C.muted }}> · opcional</span>}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{p.sensible ? '🔒' : ''} Editar</span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>{<Ic n="Lock" chico />} CLABE, identificación y comprobantes solo los ven tú y administración. Nunca salen en PDFs ni cupones.</div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div style={tarjeta}>
      <ModalError C={C} error={errorModal} onCerrar={() => setErrorModal(null)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{modo === 'corregir' ? 'Mi ficha' : 'Completa tu ficha'}</div>
        {onCerrar && <button type="button" onClick={onCerrar} aria-label="Cerrar" style={{ background: 'none', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer' }}>{<Ic n="X" chico />}</button>}
      </div>
      {modo !== 'corregir' && (ficha && ficha.reabierta_motivo && !ficha.completada_en
        ? <div className="au-banner" style={{ fontSize: 14, marginBottom: 10, background: `${C.yellow}14`, border: `1px solid ${C.yellow}44`, color: C.text }}><strong>Administración te pidió actualizar tu ficha:</strong> {ficha.reabierta_motivo}{ficha.reabierta_por ? <span style={{ color: C.muted }}> · {ficha.reabierta_por}</span> : null}. Tu ficha viene prellenada; corrige lo que haga falta y vuelve a enviarla (quedará como v{ficha.version || 2}).</div>
        : <div style={{ fontSize: 14, color: C.muted, marginBottom: 10 }}>Administración te pidió tu ficha{agente.ficha_solicitada_en ? ` (${fmtFechaCorta(agente.ficha_solicitada_en)})` : ''}. Es un dato a la vez; puedes salir y seguir después.</div>)}
      <div aria-label={`Paso ${paso.n} de ${TOTAL_PASOS}`} style={{ height: 6, borderRadius: 3, background: C.border, overflow: 'hidden', marginBottom: 6 }}>
        <div style={{ height: '100%', width: `${avance}%`, background: 'var(--equipo-grad)', transition: 'width .3s' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 14 }}>
        <span>Paso {paso.n} de {TOTAL_PASOS}</span>
        <span style={{ color: faltan.length ? C.yellow : C.green, fontWeight: 600 }}>{faltan.length ? `Faltan ${faltan.length} obligatorio${faltan.length !== 1 ? 's' : ''}` : 'Obligatorios completos'}</span>
      </div>
      <BandaBorrador C={C} borrador={borrador} compacta />
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{paso.titulo}{paso.obligatorio ? <span style={{ color: C.rosa }}> *</span> : <span style={{ color: C.muted, fontWeight: 400, fontSize: 12 }}> · opcional</span>}</div>
      {paso.ayuda && <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{paso.ayuda}</div>}
      {err && <div className="au-banner au-banner--error" style={{ fontSize: 12, marginBottom: 10 }}>{err}</div>}
      {cuerpo()}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button type="button" style={btnGhost} disabled={busy || idx === 0} onClick={() => { setArchivoNuevo(null); setErr(''); setIdx((i) => Math.max(0, i - 1)); }}>← Atrás</button>
        {paso.clave === 'revision'
          ? <button type="button" style={{ ...btnPrimario, opacity: faltan.length ? 0.5 : 1 }} disabled={busy || faltan.length > 0} onClick={enviar}>{busy ? 'Enviando…' : 'Enviar ficha'}</button>
          : (<>
            {!paso.obligatorio && <button type="button" style={btnGhost} disabled={busy} onClick={() => guardarYSeguir(true)}>Omitir</button>}
            <button type="button" style={btnPrimario} disabled={busy} onClick={() => guardarYSeguir(false)}>{busy ? 'Guardando…' : 'Guardar y seguir →'}</button>
          </>)}
      </div>
    </div>
  );
}
